import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';

import { removeAvalaraPrefix } from '../../shared/helpers';
import Loading from '../components/loading';
import OfferPage from '../components/offer';
import OrderConfirmation from '../components/offer/order-confirmation';
import OrderReview from '../components/offer/order-review';
import OfferPlanSelect from '../components/offer/plan-select';
import SettingUp from '../components/offer/setting-up/index';
import ThankYouPage from '../components/offer/thank-you';
import PageNotFound from '../components/page-not-found';
import { useAbandonedCart } from '../components/shared/hooks';
import { setHeaderProductName } from '../lib/aui-header';
import { checkIsAuthenticated } from '../lib/helpers';
import { getLoginFromUrl } from '../lib/side-effects';
import { getSystemErrorMessaging } from '../lib/side-effects/error';
import { fetchOffer } from '../lib/side-effects/offers';
import { initializeOfferProgressBar } from '../lib/side-effects/progress-bar';
import { selectState } from '../selectors';
import { shouldRedirectToLogin } from '../selectors/offer';
import { getSelectedPlan } from '../selectors/order';

import { Pages } from '@avalara/types/offers';
import type { Features } from '@avalara/types/reducer-states';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { configureFeatures } from '../lib/side-effects/features';

/** When `/api/features` has merged toggles but omits `enableAvataxFreeTrial`, behaves as false (classic `free-trial`). */
const isAvataxExtendedFreeTrialEnabled = (features: Features): boolean =>
  !!(features.ENABLEAVATAXFREETRIAL ?? features.enableAvataxFreeTrial);

/** For `/free-trial` and `/avatax-free-trial` only — wait until we have merged feature payloads (same gate as Loading). */
const resolveFetchedOfferSlug = (
  requestedOfferFlow: string,
  features: Features,
  featuresLoaded: boolean,
): string | null => {
  const isForkedTrialUrl = requestedOfferFlow === 'free-trial' || requestedOfferFlow === 'avatax-free-trial';
  if (!isForkedTrialUrl) return requestedOfferFlow;
  if (!featuresLoaded) return null;
  return isAvataxExtendedFreeTrialEnabled(features) ? 'avatax-free-trial' : 'free-trial';
};

const OfferContainer = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { requestedOfferFlow } = useParams<{ requestedOfferFlow?: string }>();
  const state = useSelector(selectState);
  const {
    account: { isLoggedIn },
    data: { featuresLoaded },
    features,
    offer: { fetchStatus, offerFlow, offerName, pages, plans },
    order: { planSelectComplete },
    system: { accountFetchFailed },
  } = state;

  const enableAvataxFf = isAvataxExtendedFreeTrialEnabled(features);

  const selectedPlan = useSelector(getSelectedPlan);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    if (fetchStatus === 'SUCCESS') getLoginFromUrl();
  }, [location.search, fetchStatus]);

  const authRedirectPending = useSelector(shouldRedirectToLogin);

  // biome-ignore lint/correctness/useExhaustiveDependencies: forks on Avatax FF + feature hydration booleans only
  useEffect(() => {
    if (!requestedOfferFlow) return;
    const slug = resolveFetchedOfferSlug(requestedOfferFlow, features, featuresLoaded);
    if (slug == null) return;
    dispatch(fetchOffer(slug));
  }, [dispatch, requestedOfferFlow, featuresLoaded, enableAvataxFf]);

  /** Canonicalize `/free-trial` ↔ `/avatax-free-trial` pathname once the hydrated offer differs. */
  useEffect(() => {
    if (fetchStatus !== 'SUCCESS' || !offerFlow) return;
    const pathname = location.pathname ?? '';
    const segments = pathname.split('/').filter(Boolean);
    const pathSlug = segments[0];
    if (pathSlug !== 'free-trial' && pathSlug !== 'avatax-free-trial') return;
    if (pathSlug === offerFlow) return;
    const nextPathname = pathname.replace(`/${pathSlug}`, `/${offerFlow}`);
    history.replace({ pathname: nextPathname, search: location.search });
  }, [fetchStatus, offerFlow, location.pathname, location.search, history]);

  // Ensure features are loaded
  useEffect(() => {
    dispatch(configureFeatures(location.search));
  }, [location.search]);

  // Access gate for `/free-trial` only (independent from `enableAvataxFreeTrial` fork below).
  // Computed inline during render (not via useState+useEffect): an effect-derived version lags one
  // render behind whenever featuresLoaded flips from false to true, because the effect that would
  // recompute it doesn't run until after that render commits. PageNotFound's <Redirect> mounts as
  // part of that same commit (a class-component componentDidMount, not a passive effect) and wins
  // the race, navigating away before the lagging effect ever gets a chance to unblock. Deriving the
  // value inline means the very render that first sees featuresLoaded===true already has the right
  // answer. Blocked while features haven't loaded yet; once loaded, only an *explicit* `false` for
  // enableGenesisFreeTrial/ENABLEGENESISFREETRIAL acts as a kill switch -- a flag that's merely
  // missing from a successful /api/features response (delivery gap, flag not configured for this
  // environment, etc.) must not block the route forever.
  const genesisFreeTrialFlag = features.enableGenesisFreeTrial ?? features.ENABLEGENESISFREETRIAL;
  const hasFreeTrialBypassParam = /a1F9xY7pQ3tV0LzW8rKd5Jc2hBnSmE4o=true/i.test(location.search);
  const shouldBlockFreeTrial = !featuresLoaded || (genesisFreeTrialFlag === false && !hasFreeTrialBypassParam);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    if (offerFlow) dispatch(initializeOfferProgressBar());
  }, [isLoggedIn, offerFlow, selectedPlan]);

  useEffect(() => {
    if (offerName) setHeaderProductName(removeAvalaraPrefix(offerName));
  }, [offerName]);

  useAbandonedCart(true);

  // NOTE: (Marlene) Using obligations to determine which ineligibility error message
  // to show so need to wait for them to be fetched
  const isAccountFetchInProgress = checkIsAuthenticated() && !isLoggedIn;
  const isIneligible = fetchStatus === 'SUCCESS' && plans.length === 0;
  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    if (!isIneligible || authRedirectPending || isAccountFetchInProgress) return;
    const errorCode = 'defaultIneligibility';
    dispatch(getSystemErrorMessaging({ errorCode, offerName }));
  }, [isIneligible, authRedirectPending, offerFlow, isAccountFetchInProgress]);

  // Wait for features to load before processing any logic
  if (!featuresLoaded) {
    return <Loading />;
  }

  // Do not mount offer routes until account fetch has completed (success or accountFetchFailed).
  // Otherwise components may trigger AUI/host requests (e.g. companies) and cause a reload loop.
  if (offerFlow && isAccountFetchInProgress && !accountFetchFailed) {
    return <Loading />;
  }

  // Account has no Avatax (e.g. Identity session but accountId 0). Don't mount offer routes.
  // App shows the error as a dialog (getSystemErrorMessaging already dispatched).
  if (accountFetchFailed) return null;

  // Block free-trial access per shouldBlockFreeTrial above. Scoped to the free-trial route itself --
  // must not affect any other offerFlow.
  if (requestedOfferFlow === 'free-trial' && shouldBlockFreeTrial) {
    return <PageNotFound />;
  }

  if (fetchStatus === 'PENDING' || isIneligible) return null;
  if (fetchStatus === 'NOT_FOUND' || !offerFlow) return <PageNotFound />;
  if (authRedirectPending) return <Loading />;

  return (
    <Switch>
      <Route exact path={`/${offerFlow}`} component={OfferPlanSelect} />
      {pages.includes(Pages.OrderReview) && <Route path={`/${offerFlow}/order-review`} component={OrderReview} />}
      {pages.includes(Pages.OrderReview) && <Route path={`/${offerFlow}/thank-you`} component={OrderConfirmation} />}
      {!planSelectComplete && (
        <Route path={`/${offerFlow}`}>
          <Redirect to={{ pathname: `/${offerFlow}`, search: window.location.search }} />
        </Route>
      )}
      {pages.includes(Pages.SettingUp) && <Route path={`/${offerFlow}/setting-up`} component={SettingUp} />}
      <Route path={`/${offerFlow}/thank-you`} component={ThankYouPage} />
      <Route component={OfferPage} />
    </Switch>
  );
};

export default OfferContainer;
